import {ReactElement, useCallback} from "react";
import {FormGroup} from "components/UIComponents/FormGroup";
import {FormattedMessage} from "react-intl";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {SubTitle} from "components/UIComponents/SubTitle";
import {assoc} from "ramda";
import {PillSelector} from "components/UIComponents/PillSelector";
import {EditorItem} from "../../editorUtils";
import {ImageItem} from "../../MenuDocumentModel";
import {menuEditorActions, menuEditorSelectors} from "../../editorSlice";
import {EditorGroup} from "../EditorGroup";
import {ImagePicker} from "./ImagePicker";
import {Slider} from "components/UIComponents/Slider";
import {fetchImageCollection, ImageCollection, ImageGroup} from "./imageCollections";
import { authSelectors } from "features/auth/authSlice";

export interface ImageEditorProps {
    image: EditorItem<ImageItem>
}

export function ImageEditor(props: ImageEditorProps): ReactElement {
    const document = useAppSelector(menuEditorSelectors.document);
    const isAperitif = document?.type === 'aperitif';
    const collection: ImageCollection = isAperitif?'aperitif_filler':'filler';

    const dispatch = useAppDispatch();

    const itemKey = props.image.itemKey;
    const imageItem = props.image.menuItem;

    const userId = useAppSelector(authSelectors.customer);

    const updateHeight = (height: number) => {
        dispatch(menuEditorActions.editItem({
            key: itemKey,
            newItem: assoc('height', height, imageItem)
        }));
    }

    const updateUrl = (url: string | null) => {
        if (url != null) {
            dispatch(menuEditorActions.editItem({
                key: itemKey,
                newItem: assoc('url', url, imageItem)
            }));
        }
    }

    const updateAlignment = (align: 'start' | 'center' | 'end') => {
        dispatch(menuEditorActions.editItem({
            key: itemKey,
            newItem: assoc('align', align, imageItem)
        }));
    }

    const fetchFillerImageCollection = useCallback((): Promise<ImageGroup[]> =>
        userId ?
            fetchImageCollection({
                userId,
                collection: collection
            })
            : Promise.resolve([]),
        [userId, collection]
    )

    if (userId === null) return <></>;

    return (
        <EditorGroup>
            <SubTitle><FormattedMessage id="editor.image"/></SubTitle>
            <FormGroup groupTitle={<><FormattedMessage id="editor.image.height"/> ({imageItem.height}mm)</>}>
                <Slider
                    min={8}
                    max={170}
                    value={imageItem.height}
                    onChange={updateHeight}
                />
            </FormGroup>
            <FormGroup groupTitle={<FormattedMessage id="editor.image.alignment"/>}>
                <PillSelector
                    options={['start', 'center', 'end']}
                    selected={imageItem.align || 'start'}
                    onSelect={v => updateAlignment(v as ('start' | 'center' | 'end'))}
                    getLabel={v => <FormattedMessage id={`editor.image.${v}`}/>}
                />
            </FormGroup>
            <FormGroup groupTitle={<FormattedMessage id="editor.image"/>}>
                <ImagePicker
                    images={fetchFillerImageCollection}
                    currentImage={imageItem.url}
                    onPick={updateUrl}
                    allowClear={false}
                    upload={{
                        userId: userId,
                        collection: collection
                    }}
                />
            </FormGroup>
        </EditorGroup>
    );
}